import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronsUpDown, Plus, User } from 'lucide-react'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import NewTeamModal from './NewTeamModal'
import { useState } from 'react'
import ProtectedContent, {
  hasAccessToContent,
} from 'app/frontend/base/components/ProtectedContent'
import { Badge } from '@/components/ui/badge'
import { compareTeamsByDefaultAndName } from 'app/frontend/base/helpers/teamOrdering'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import Card from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'

const TeamSwitcher = () => {
  const { t } = useTranslation()
  const { guild, currentTeam, teamReference } = useGuild()
  const { customDomain } = useCustomDomain()
  const [creatingTeam, setCreatingTeam] = useState(false)
  const navigate = useNavigate()
  const hasGuildCustomDomain = customDomain?.guild && !customDomain.team
  const guildOverviewPath = hasGuildCustomDomain ? '/' : guild?.path

  const handleTeamSelect = (team: wowaudit.TeamReference) => {
    const currentPath = window.location.pathname.replace(
      currentTeam?.path ?? '',
      '',
    )

    navigate(`${team.path}${currentTeam ? currentPath : ''}`)
  }

  const teamsWithAccess = [...(guild?.teamReferences || [])]
    .filter(
      (team) => hasAccessToContent('member', 'base', guild, team).hasAccess,
    )
    .sort(compareTeamsByDefaultAndName)

  const content = (
    <div className={teamsWithAccess.length || currentTeam ? '' : 'invisible'}>
      {guild && !guild?.teamReferences?.length ? (
        <Button size='sm' onClick={() => setCreatingTeam(true)}>
          {t('dashboard.team.create-first-team')}
        </Button>
      ) : (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild disabled={!teamsWithAccess.length}>
            <span
              className={`inline-flex ${
                !teamsWithAccess.length ? 'cursor-default' : 'cursor-pointer'
              } text-sm items-center font-bold`}
            >
              {teamReference ? (
                <span className='whitespace-nowrap'>
                  {t('shared.team')} {teamReference.name}
                </span>
              ) : (
                <span className='text-muted-foreground'>
                  {t('dashboard.team.choose-team')}
                </span>
              )}
              {teamsWithAccess.length ? (
                <ChevronsUpDown className='ml-2' width={14} />
              ) : null}
            </span>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className='min-w-(--radix-dropdown-menu-trigger-width)'
            align='start'
          >
            {currentTeam && guild ? (
              <>
                <DropdownMenuItem
                  className='flex gap-1 items-center'
                  onSelect={() => navigate(guildOverviewPath ?? '/')}
                >
                  View guild overview
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}

            {teamsWithAccess.map((team) => (
              <DropdownMenuItem
                key={team.id}
                onSelect={() => handleTeamSelect(team)}
              >
                <div
                  className={`flex w-full items-center gap-4 justify-between ${
                    team.id === currentTeam?.id ? 'text-primary' : ''
                  }`}
                >
                  <span className='flex items-center gap-1.5'>
                    {team.default ? <DefaultTeamStar /> : null}
                    <span>{team.name}</span>
                  </span>
                  <Badge variant='outline' className='flex items-center gap-1'>
                    <span>{team.characterAmount}</span>
                    <User size={12} />
                  </Badge>
                </div>
              </DropdownMenuItem>
            ))}

            <ProtectedContent
              section='base'
              requirement='admin'
              renderUnauthorized={false}
              requireAllTeams={true}
            >
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className='flex gap-1 items-center'
                onClick={() => setCreatingTeam(true)}
              >
                <Plus width={14} />
                {t('dashboard.team.create-new')}
              </DropdownMenuItem>
            </ProtectedContent>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {guild ? (
        <NewTeamModal setOpen={setCreatingTeam} open={creatingTeam} />
      ) : null}
    </div>
  )

  if (hasGuildCustomDomain) {
    return (
      <Card
        className={cn(
          'w-fit py-0 px-2',
          customDomain?.headerConfig?.transparentCards
            ? 'bg-transparent border-none hover:bg-transparent'
            : '',
        )}
      >
        {content}
      </Card>
    )
  }

  return content
}

export default TeamSwitcher
